import { FC, PropsWithChildren, ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Combobox, ComboboxOption } from "@/components/_molecules/combobox/combobox";

import { ALLOWED_INDICATOR_FREQUENCIES, IndicatorFrequency } from "../models/indicator-frequency-model";

import { IndicatorFrequencySelectDropdownItem } from "./indicator-frequency-select-dropdown-item";

export interface IndicatorFrequencySelectDropdownProps extends PropsWithChildren {
  defaultValue?: IndicatorFrequency;
  onSelect?: (type: IndicatorFrequency | null) => void;
  asChild?: boolean;
  isRequired?: boolean;
}

export const IndicatorFrequencySelectDropdown: FC<IndicatorFrequencySelectDropdownProps> = ({
  children,
  asChild,
  onSelect,
  isRequired = false,
  ...props
}) => {
  const { t } = useTranslation("indicator");
  const [selectedFrequency, setSelectedFrequency] = useState<IndicatorFrequency | null>(
    props.defaultValue ?? (isRequired ? ALLOWED_INDICATOR_FREQUENCIES[0] : null),
  );

  const comboboxOptions = useMemo(() => {
    const options: ComboboxOption<IndicatorFrequency>[] = ALLOWED_INDICATOR_FREQUENCIES.map((type) => ({
      id: type,
      value: t(`frequency.${type}`),
      original: type,
    }));

    if (!isRequired) {
      return [{ id: "none", value: t("frequency.notAssigned"), original: null }, ...options];
    }

    return options;
  }, [isRequired, t]);

  const renderItem = (option: ComboboxOption<IndicatorFrequency>): ReactElement => {
    const selected = option.id === (selectedFrequency ?? "none");
    const frequency = option.original;
    return <IndicatorFrequencySelectDropdownItem frequency={frequency} selected={selected} />;
  };

  const handleSelected = (selectedOption: ComboboxOption<IndicatorFrequency> | null) => {
    if (!selectedOption) {
      return;
    }

    const type = selectedOption.original;
    setSelectedFrequency(type);
    if (onSelect) {
      onSelect(type);
    }
  };

  if (!comboboxOptions.length) {
    return <></>;
  }

  return (
    <Combobox
      asChild={asChild}
      options={comboboxOptions}
      renderItem={renderItem}
      onSelect={handleSelected}
      placeholder={t("frequency.placeholder")}
      side="bottom"
    >
      {children}
    </Combobox>
  );
};
